import { useEffect, useRef, useState } from 'react';
import { CometChatMessageEvents, CometChatUIEvents } from '@cometchat/chat-uikit-react';
import DraftService from '../services/draft.service';

const useFloatingChatDrafts = () => {
  const [currentDraft, setCurrentDraft] = useState('');
  const [currentConversation, setCurrentConversation] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentGroup, setCurrentGroup] = useState(null);
  
  const currentDraftRef = useRef(currentDraft);
  const currentConversationRef = useRef(currentConversation);
  const currentUserRef = useRef(currentUser);
  const currentGroupRef = useRef(currentGroup);

  useEffect(() => {
    currentDraftRef.current = currentDraft;
  }, [currentDraft]);
  
  useEffect(() => {
    currentConversationRef.current = currentConversation;
    currentUserRef.current = currentUser;
    currentGroupRef.current = currentGroup;
  }, [currentConversation, currentUser, currentGroup]);

  const loadDraftForConversation = (conversation, user, group) => {
    const targetUser = conversation?.getConversationType() === "user"
      ? conversation.getConversationWith()
      : user;
    const targetGroup = conversation?.getConversationType() === "group"
      ? conversation.getConversationWith()
      : group;
    
    let draft = "";
    if (targetUser) {
      draft = DraftService.getDraftForUser(targetUser.getUid());
    } else if (targetGroup) {
      draft = DraftService.getDraftForGroup(targetGroup.getGuid());
    }
    
    setCurrentDraft(draft);
    
    setTimeout(() => {
      const floatingMessageWidget = document.querySelector('.floating-message-widget-portal');
      if (!floatingMessageWidget) return;
      
      const contentEditableDiv = floatingMessageWidget.querySelector('div[contenteditable]');
      if (contentEditableDiv && draft) {
        contentEditableDiv.textContent = draft;
        contentEditableDiv.dispatchEvent(new Event('input', { bubbles: true }));
        contentEditableDiv.focus();
      }
    }, 100);
  };

  useEffect(() => {
    const chatChanged = CometChatUIEvents.ccActiveChatChanged?.subscribe?.((activeChat) => {
      if (activeChat) {
        if (activeChat.conversation) {
          setCurrentConversation(activeChat.conversation);
          setCurrentUser(null);
          setCurrentGroup(null);
          loadDraftForConversation(activeChat.conversation, null, null);
        } else if (activeChat.user) {
          setCurrentConversation(null);
          setCurrentUser(activeChat.user);
          setCurrentGroup(null);
          loadDraftForConversation(null, activeChat.user, null);
        } else if (activeChat.group) {
          setCurrentConversation(null);
          setCurrentUser(null);
          setCurrentGroup(activeChat.group);
          loadDraftForConversation(null, null, activeChat.group);
        }
      }
    });

    return () => {
      chatChanged?.unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    const handleMessageSent = (data) => {
      if (data.message?.receiverType === "group") {
        DraftService.clearDraftForGroup(data.message?.receiver?.guid);
        setCurrentDraft('');
      } else if (data.message?.receiverType === "user") {
        DraftService.clearDraftForUser(data.message?.receiver?.uid);
        setCurrentDraft('');
      }
    };

    const sentMessageSubscription = CometChatMessageEvents.ccMessageSent.subscribe(handleMessageSent);

    return () => {
      sentMessageSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let isUpdatingFromSync = false;
    
    const handleInputChange = (e) => {
      const floatingMessageWidget = document.querySelector('.floating-message-widget-portal');
      if (!floatingMessageWidget || !floatingMessageWidget.contains(e.target)) return;
      
      if (e.target.contentEditable === 'true' || e.target.contentEditable === 'plaintext-only') {
        if (isUpdatingFromSync) {
          return;
        }
        
        const text = e.target.textContent || e.target.innerText || '';
        setCurrentDraft(text);
        
        setTimeout(() => {
          const user = currentConversationRef.current?.getConversationType() === "user"
            ? currentConversationRef.current.getConversationWith()
            : currentUserRef.current;
          const group = currentConversationRef.current?.getConversationType() === "group"
            ? currentConversationRef.current.getConversationWith()
            : currentGroupRef.current;
          
          if (user) {
            DraftService.saveDraftForUser(user.getUid(), text);
          } else if (group) {
            DraftService.saveDraftForGroup(group.getGuid(), text);
          }
        }, 300);
      }
    };
    
    const handlePaste = (e) => {
      const floatingMessageWidget = document.querySelector('.floating-message-widget-portal');
      if (!floatingMessageWidget || !floatingMessageWidget.contains(e.target)) return;
      
      if (e.target.contentEditable === 'true' || e.target.contentEditable === 'plaintext-only') {
        setTimeout(() => {
          handleInputChange(e);
        }, 0);
      }
    };
    
    const handleDraftSync = (e) => {
      const { key, newValue } = e.detail;
      
      const user = currentConversationRef.current?.getConversationType() === "user"
        ? currentConversationRef.current.getConversationWith()
        : currentUserRef.current;
      const group = currentConversationRef.current?.getConversationType() === "group"
        ? currentConversationRef.current.getConversationWith()
        : currentGroupRef.current;
      
      let isForCurrentConversation = false;
      if (user && key === `cometchat_draft_user_${user.getUid()}`) {
        isForCurrentConversation = true;
      } else if (group && key === `cometchat_draft_group_${group.getGuid()}`) {
        isForCurrentConversation = true;
      }
      
      if (isForCurrentConversation) {
        const floatingMessageWidget = document.querySelector('.floating-message-widget-portal');
        if (floatingMessageWidget) {
          const contentEditableDiv = floatingMessageWidget.querySelector('div[contenteditable]');
          if (contentEditableDiv) {
            const currentText = contentEditableDiv.textContent || contentEditableDiv.innerText || '';
            const targetText = newValue || '';
            
            if (currentText !== targetText && document.activeElement !== contentEditableDiv) {
              isUpdatingFromSync = true;
              
              const selection = window.getSelection();
              const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
              const cursorOffset = range ? range.startOffset : 0;
              
              contentEditableDiv.textContent = targetText;
              setCurrentDraft(targetText);
              
              if (range && contentEditableDiv.firstChild) {
                const newRange = document.createRange();
                const textNode = contentEditableDiv.firstChild;
                const maxOffset = Math.min(cursorOffset, textNode.textContent.length);
                newRange.setStart(textNode, maxOffset);
                newRange.setEnd(textNode, maxOffset);
                selection.removeAllRanges();
                selection.addRange(newRange);
              }
              
              setTimeout(() => {
                isUpdatingFromSync = false;
              }, 100);
            }
          }
        }
      }
    };
    
    const handleSendClick = (e) => {
      const floatingMessageWidget = document.querySelector('.floating-message-widget-portal');
      if (!floatingMessageWidget || !floatingMessageWidget.contains(e.target)) return;
    };
    
    document.addEventListener('input', handleInputChange, true);
    document.addEventListener('paste', handlePaste, true);
    document.addEventListener('click', handleSendClick, true);
    window.addEventListener('draftChanged', handleDraftSync);
    
    return () => {
      document.removeEventListener('input', handleInputChange, true);
      document.removeEventListener('paste', handlePaste, true);
      document.removeEventListener('click', handleSendClick, true);
      window.removeEventListener('draftChanged', handleDraftSync);
    };
  }, []);

  return {
    currentDraft,
    loadDraftForConversation
  };
};

export default useFloatingChatDrafts;